import React, { useEffect, useState } from "react";
import StageEffects from "@/components/StageEffects";
import StageVisual from "@/components/StageVisual";
import JarvisOrb from "@/components/JarvisOrb";
import ProofCanvas from "@/components/ProofCanvas";
import ThemedBackground from "@/components/ThemedBackground";
import BackgroundWidgets from "@/components/BackgroundWidgets";
import useTheme from "@/hooks/useTheme";

// Mirror display — open this route in a second window/screen.
// Listens on the "rawco-stage" BroadcastChannel for state pushed by the main console.
export default function Display() {
  const [visual, setVisual] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [orb, setOrb] = useState({ active: false, listening: false, speaking: false, thinking: false });
  const [connected, setConnected] = useState(false);
  const [showOrb, setShowOrb] = useState(false);
  const [now, setNow] = useState(new Date());
  const [lightningTrigger, setLightningTrigger] = useState(0);
  const themeHook = useTheme();
  const bcRef = React.useRef(null);

  // onSend — fired when the owner clicks a pill in the architecture
  // diagram (or any other interactive panel in the mirror). Posts a
  // `command` message back to the orb window; App.js listens and runs it
  // through jarvis.sendText so the agent responds out loud.
  const sendToOrb = React.useCallback((cmd) => {
    try { bcRef.current?.postMessage({ type: "command", text: cmd }); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("orb") === "1") setShowOrb(true);

    const bc = new BroadcastChannel("rawco-stage");
    bcRef.current = bc;
    bc.onmessage = (e) => {
      const msg = e.data || {};
      if (msg.type === "visual") {
        setVisual(msg.visual);
        setLightningTrigger(t => t + 1);
        // Clear lingering proof cards from the previous topic — was causing
        // the morning_brief stack to bury new panels (agent_blueprint etc).
        window.dispatchEvent(new Event("rawco-proof-clear"));
      }
      else if (msg.type === "clear") setVisual(null);
      else if (msg.type === "clear_proof") window.dispatchEvent(new Event("rawco-proof-clear"));
      else if (msg.type === "transcript") setTranscript(msg.text || "");
      else if (msg.type === "reply") setReply(msg.text || "");
      else if (msg.type === "orb") setOrb(msg.state || {});
      else if (msg.type === "lightning_strike") setLightningTrigger(t => t + 1);
      else if (msg.type === "theme-changed") themeHook.refresh();
      else if (msg.type === "widgets-changed") window.dispatchEvent(new Event("rawco-widgets-changed"));
      else if (msg.type === "ping") bc.postMessage({ type: "pong" });
    };
    // Request a state replay from the main window
    bc.postMessage({ type: "request_state" });
    // ⚡ Owner Feb 2026: "my mirror still doesn't show the brief
    // automatically when I log in". Root cause: the orb's boot sequence
    // has 4 silent guards (visual already showing, user interacted,
    // jarvis speaking, 30s lock) that block the auto-brief on anything
    // that isn't a fresh sign-in. The MIRROR opening should ALWAYS
    // trigger a brief regardless. We send an explicit "request_brief"
    // message; App.js listens for it and fires sendText("morning brief")
    // bypassing all the guards. 2s delay so the orb's BC handler is up
    // and the initial state replay has landed first.
    const briefTimer = setTimeout(() => {
      try { bc.postMessage({ type: "request_brief", source: "mirror_mount" }); } catch { /* ignore */ }
    }, 2000);

    // Track connection: main window replies "pong" to our pings
    let alive = false;
    const onPong = (e) => { if ((e.data || {}).type === "pong") alive = true; };
    bc.addEventListener("message", onPong);

    const tick = setInterval(() => {
      alive = false;
      bc.postMessage({ type: "ping" });
      setTimeout(() => setConnected(alive), 600);
    }, 4000);

    const clock = setInterval(() => setNow(new Date()), 1000);

    return () => {
      bc.close();
      clearInterval(tick);
      clearInterval(clock);
      clearTimeout(briefTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timeStr = now.toTimeString().slice(0, 8);
  const dateStr = now.toDateString().toUpperCase();

  return (
    <div className="App min-h-screen relative overflow-hidden" data-testid="display-page">
      <ThemedBackground background={themeHook?.theme?.background} lite={true} />
      <StageEffects trigger={lightningTrigger} />
      {/* Voice-added widget gallery (Racing/Raw Mixes/Weather/SnapDog strip)
          lives ONLY on this mirror screen. Orb stays clean per owner contract. */}
      <BackgroundWidgets />

      {/* Optional orb mirror */}
      {showOrb && (
        <div className="fixed inset-0 z-[2] flex items-center justify-center pointer-events-none">
          <div className="relative w-[min(60vw,520px)] h-[min(60vw,520px)]">
            <JarvisOrb active={orb.active} listening={orb.listening} />
          </div>
        </div>
      )}

      {/* Status bar */}
      <div className="stage-status" data-testid="display-status">
        <span className="rc-label text-[10px]">{connected ? "// MIRROR · CONNECTED" : "// MIRROR · WAITING FOR PRIMARY"}</span>
        <span className={`w-2 h-2 ${connected ? "bg-[var(--rc-gold)] rc-pulse-glow" : "bg-[var(--rc-gold-dim)]"}`} />
        <div className="ml-3 text-right">
          <div className="font-display rc-glow-text text-sm tabular-nums">{timeStr}</div>
          <div className="rc-label text-[9px]">{dateStr}</div>
        </div>
      </div>

      {/* Stage visual mirror */}
      <div className="stage-visual" style={{ zIndex: visual ? 600 : 1 }}>
        {visual ? (
          <StageVisual
            visual={visual}
            dismiss={() => setVisual(null)}
            onSend={sendToOrb}
            /* ⚡ CLARIFY RELIABILITY — owner complaint: "the clarify
               buttons don't work, I reply by voice and it doesn't carry
               on". Root cause: onChoice was undefined on the mirror's
               StageVisual mount, so button clicks called
               `undefined?.(opt)` and silently did nothing. We now route
               the choice as a command string back to the orb so the
               brain treats it as the next user message (with full chat
               history context, so "ghost story" continues the previous
               clarify naturally). The card is dismissed locally too so
               the owner sees the choice was registered. */
            onChoice={(opt) => {
              try { sendToOrb(opt); } catch { /* ignore */ }
              setVisual(null);
            }}
          />
        ) : (
          /* No active card → faint watermark only, behind widgets. Owner:
             "RAW-DATA logo is shining in front of a widget". */
          <div className="text-center pointer-events-none" style={{ opacity: 0.25 }}>
            <div className="font-display text-3xl rc-glow-text tracking-widest">RAW-DATA</div>
            <div className="rc-label text-[10px] mt-2 opacity-60">SECONDARY DISPLAY · AWAITING SIGNAL</div>
          </div>
        )}
      </div>

      {/* Subtitles removed by owner request — voice is the channel. */}

      {/* Multi-window proof canvas — cards open here as R.A.W. narrates.
          User can drag/close any card individually. */}
      <ProofCanvas mode="mirror" />
    </div>
  );
}
